/**
 * ScoringEmphasisPanel — unified scoring emphasis control for the Coaching Studio.
 *
 * RECONCILIATION NOTE (2026-06-03):
 * The previous "Full-Body Baseline" slider (globalBodyMatchWeight) and the new
 * "Scoring Emphasis" control would have been duplicate controls: both mapped to the
 * single goals-vs-body-form axis. This panel REPLACES the Full-Body Baseline slider
 * in ScoringStudio.tsx. The GlobalBodyMatchSettings component is no longer rendered;
 * instead, ScoringEmphasisPanel owns the goal_blend/body_form axis and the eom_blend
 * axis in a single unified control.
 *
 * PAYLOAD ROUND-TRIP:
 * - The `globalBodyMatchWeight` prop written to `metadata.global_body_match_weight`
 *   in CreateTemplate.tsx is PRESERVED UNCHANGED. The panel's "My Goals vs. Full-Body
 *   Form" axis maps 1:1 to this field: bodyFormWeight = globalBodyMatchWeight.
 *   When goal_blend (backend concept) is 0.6, the body form weight is 0.4 — so
 *   globalBodyMatchWeight = (1 - goal_blend). Presets below set concrete values.
 * - The `economyOfMotionWeight` prop maps to `metadata.economy_of_motion_weight`.
 *   No new fields are added to the payload; the same two callbacks are passed in.
 * - The `globalBodyMatchEnabled`/`onGlobalBodyMatchEnabledChange` props are retained
 *   for backward compat (the backend still reads global_body_match_enabled), but the
 *   toggle is now surfaced here instead of inside GlobalBodyMatchSettings.
 *
 * DEPRECATED PARAMS NOT EXPOSED:
 * - temporal_weight is always 0.0 (deprecated, scoring_config.py:737) — no slider
 * - global_body_match_weight as a separate "Full Body Baseline" concept no longer
 *   exists as its own section; it is the inverse of goal_blend, presented here as
 *   the My Goals ⇄ Full-Body Form axis
 */

import { useState, useCallback, useMemo } from 'react';
import { cn } from '@/utils/cn';
import {
  InformationCircleIcon,
  ChevronDownIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { SNAP_CONFIG } from '@/utils/constants';
import { type ScoringPreset, type PresetConfig, SCORING_PRESETS } from './scoringPresets';
import {
  CHECKPOINTS_CARRY_NO_WEIGHT_LINE,
  checkpointsCarryNoWeight,
} from './scoringEmphasisAdvisory';

// Tolerance used to detect if current slider values match a preset
const PRESET_MATCH_TOLERANCE = 0.02;

function detectPreset(bodyFormWeight: number, eomWeight: number): ScoringPreset {
  for (const preset of SCORING_PRESETS) {
    if (
      Math.abs(bodyFormWeight - preset.bodyFormWeight) <= PRESET_MATCH_TOLERANCE &&
      Math.abs(eomWeight - preset.eomWeight) <= PRESET_MATCH_TOLERANCE
    ) {
      return preset.id;
    }
  }
  return 'custom';
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ScoringEmphasisPanelProps {
  /**
   * Body form weight (0–1). Maps to `metadata.global_body_match_weight`.
   * Inverse of backend goal_blend: bodyFormWeight = 1 - goal_blend.
   * Default: 0.4 (corresponds to goal_blend=0.6).
   */
  bodyFormWeight: number;
  onBodyFormWeightChange: (weight: number) => void;

  /** Whether body-form scoring is enabled. Maps to `metadata.global_body_match_enabled`. */
  bodyFormEnabled: boolean;
  onBodyFormEnabledChange: (enabled: boolean) => void;

  /**
   * Economy of Motion influence weight (0–0.5).
   * Maps to `metadata.economy_of_motion_weight` / backend eom_blend.
   * Default: 0.10.
   */
  eomWeight: number;
  onEomWeightChange: (weight: number) => void;

  /**
   * The template's annotations, read ONLY to decide whether the coach's
   * checkpoints currently carry any weight (owner decision D-A (b),
   * 2026-10-09). Never mutated and never rendered. Optional so an existing
   * caller that has no annotations to hand keeps working — the advisory simply
   * stays silent, which is the honest answer when we cannot count checkpoints.
   *
   * The condition itself lives in `scoringEmphasisAdvisory.ts` and mirrors
   * `template_validation._check_checkpoints_carry_no_weight` clause for clause.
   */
  annotations?: readonly unknown[];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ScoringEmphasisPanel({
  bodyFormWeight,
  onBodyFormWeightChange,
  bodyFormEnabled,
  onBodyFormEnabledChange,
  eomWeight,
  onEomWeightChange,
  annotations,
}: ScoringEmphasisPanelProps) {
  // Owner decision D-A (b), 2026-10-09 — the coach's checkpoints carry zero
  // weight at Body Form 100%. Same four-clause condition as the backend's
  // finalize warning; see `scoringEmphasisAdvisory.ts`.
  const noWeightForCheckpoints = useMemo(
    () =>
      checkpointsCarryNoWeight({
        bodyFormEnabled,
        bodyFormWeight,
        annotations: annotations ?? [],
      }),
    [bodyFormEnabled, bodyFormWeight, annotations]
  );

  // Open the Advanced disclosure on mount when the checkpoints are already
  // inert: the slider is both the cause and the fix, so a coach who opens a
  // template in that state must be able to SEE the line that explains it.
  // Initial state only — it never re-collapses or re-opens as the coach drags.
  const [advancedExpanded, setAdvancedExpanded] = useState(noWeightForCheckpoints);

  const activePreset = useMemo(
    () => detectPreset(bodyFormWeight, eomWeight),
    [bodyFormWeight, eomWeight]
  );

  const applyPreset = useCallback(
    (preset: PresetConfig) => {
      onBodyFormWeightChange(preset.bodyFormWeight);
      onEomWeightChange(preset.eomWeight);
      // Ensure body form scoring is enabled when a preset is applied
      if (!bodyFormEnabled) {
        onBodyFormEnabledChange(true);
      }
    },
    [onBodyFormWeightChange, onEomWeightChange, bodyFormEnabled, onBodyFormEnabledChange]
  );

  const handleBodyFormSlider = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = parseFloat(e.target.value);
      const snapPoints = [0, 0.2, 0.25, 0.4, 0.5, 0.7, 0.75, 1.0];
      for (const point of snapPoints) {
        if (Math.abs(value - point) <= SNAP_CONFIG.SLIDER_PERCENT) {
          onBodyFormWeightChange(point);
          return;
        }
      }
      onBodyFormWeightChange(value);
    },
    [onBodyFormWeightChange]
  );

  const handleEomSlider = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = parseFloat(e.target.value);
      const snapPoints = [0, 0.05, 0.10, 0.15, 0.20, 0.25, 0.30];
      for (const point of snapPoints) {
        if (Math.abs(value - point) <= SNAP_CONFIG.SLIDER_PERCENT) {
          onEomWeightChange(point);
          return;
        }
      }
      onEomWeightChange(value);
    },
    [onEomWeightChange]
  );

  // Derived display values
  const goalBlendPct = Math.round((1 - bodyFormWeight) * 100);
  const bodyFormPct = Math.round(bodyFormWeight * 100);
  const eomPct = Math.round(eomWeight * 100);

  return (
    <div data-testid="scoring-emphasis-panel" className="space-y-4">
      {/* Preset cards */}
      <div>
        <p className="text-sm font-medium text-text-secondary mb-3">
          Choose a scoring emphasis
        </p>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {SCORING_PRESETS.map((preset) => {
            const isActive = activePreset === preset.id;
            return (
              <button
                key={preset.id}
                type="button"
                onClick={() => applyPreset(preset)}
                data-testid={`preset-${preset.id}`}
                className={cn(
                  'relative flex items-start gap-3 rounded-xl border-2 p-3 text-left',
                  'transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent',
                  isActive
                    ? 'border-accent bg-accent/10'
                    : 'border-border-subtle bg-dark-elevated-1 hover:border-accent/40 hover:bg-dark-elevated-2'
                )}
              >
                {/* Checkmark when active */}
                <span
                  className={cn(
                    'mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full',
                    isActive
                      ? 'bg-accent text-dark-bg'
                      : 'border border-border-subtle bg-dark-elevated-2'
                  )}
                >
                  {isActive && <CheckIcon className="h-3 w-3" />}
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-text-primary">{preset.label}</p>
                  <p className="mt-0.5 text-xs text-text-muted leading-relaxed">
                    {preset.description}
                  </p>
                </div>
              </button>
            );
          })}
        </div>

        {/* "Custom" indicator shown when sliders diverge from all presets */}
        {activePreset === 'custom' && (
          <div className="mt-2 flex items-center gap-1.5">
            <span className="inline-flex items-center rounded-full bg-dark-elevated-2 px-2.5 py-0.5 text-xs font-medium text-text-secondary">
              Custom
            </span>
            <span className="text-xs text-text-muted">
              Slider values don't match any preset.
            </span>
          </div>
        )}
      </div>

      {/* Blend summary chips */}
      <div className="flex flex-wrap gap-2">
        <span className="inline-flex items-center rounded-full bg-accent/15 px-3 py-1 text-xs font-medium text-accent">
          My Goals: {goalBlendPct}%
        </span>
        <span className="inline-flex items-center rounded-full bg-dark-elevated-2 px-3 py-1 text-xs font-medium text-text-secondary">
          Body Form: {bodyFormPct}%
        </span>
        {eomWeight > 0 && (
          <span className="inline-flex items-center rounded-full bg-info/15 px-3 py-1 text-xs font-medium text-info">
            Movement Quality: {eomPct}%
          </span>
        )}
      </div>

      {/* What this affects explanation */}
      <div className="flex items-start gap-2 rounded-lg border border-border-subtle bg-dark-elevated-1 p-3">
        <InformationCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-text-muted" />
        <p className="text-xs text-text-secondary leading-relaxed">
          These settings change how the athlete's final score is calculated.
          Higher "My Goals" weight means your specific coaching checkpoints
          (distance, axis, timing, joint angle) matter more. Higher "Body Form"
          weight means overall body shape similarity to the template matters more.
        </p>
      </div>

      {/* Advanced sliders disclosure */}
      <div className="rounded-lg border border-border-subtle overflow-hidden">
        <button
          type="button"
          onClick={() => setAdvancedExpanded((v) => !v)}
          aria-expanded={advancedExpanded}
          className={cn(
            'flex w-full items-center justify-between gap-2 p-3',
            'text-sm font-medium text-text-secondary',
            'transition-colors duration-150 hover:bg-dark-elevated-1',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent'
          )}
        >
          <span>Advanced — adjust manually</span>
          <ChevronDownIcon
            className={cn(
              'h-4 w-4 flex-shrink-0 transition-transform duration-200 ease-out',
              advancedExpanded && 'rotate-180'
            )}
          />
        </button>

        <div
          className={cn(
            'grid transition-[grid-template-rows] duration-200 ease-out',
            advancedExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
          )}
        >
          <div className="overflow-hidden">
            <div className="space-y-5 px-4 pb-4 pt-1">

              {/* Body form enable toggle */}
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-text-primary">
                    Include Body Form scoring
                  </p>
                  <p className="text-xs text-text-muted mt-0.5">
                    When off, only your annotation goals count toward the score.
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={bodyFormEnabled}
                  onClick={() => onBodyFormEnabledChange(!bodyFormEnabled)}
                  className={cn(
                    'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full',
                    'border-2 border-transparent transition-colors duration-200 ease-in-out',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2',
                    bodyFormEnabled ? 'bg-accent' : 'bg-dark-elevated-2'
                  )}
                >
                  <span
                    className={cn(
                      'pointer-events-none inline-block h-5 w-5 transform rounded-full',
                      'bg-white shadow ring-0 transition duration-200 ease-in-out',
                      bodyFormEnabled ? 'translate-x-5' : 'translate-x-0'
                    )}
                  />
                </button>
              </div>

              {/* My Goals ⇄ Full-Body Form slider */}
              {bodyFormEnabled && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label
                      className="text-sm font-medium text-text-secondary"
                      title="How much your specific annotation goals matter vs. overall body shape similarity"
                    >
                      My Goals ⇄ Full-Body Form
                    </label>
                    <span className="text-sm font-semibold text-text-primary">
                      {goalBlendPct}% / {bodyFormPct}%
                    </span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={bodyFormWeight}
                    onChange={handleBodyFormSlider}
                    aria-label="Body form weight — higher values give more weight to body shape similarity"
                    className={cn(
                      'w-full h-2 rounded-full appearance-none cursor-pointer',
                      'bg-gradient-to-r from-accent via-purple-400 to-blue-400',
                      '[&::-webkit-slider-thumb]:appearance-none',
                      '[&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5',
                      '[&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white',
                      '[&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:border-2',
                      '[&::-webkit-slider-thumb]:border-border-subtle',
                      '[&::-webkit-slider-thumb]:transition-all [&::-webkit-slider-thumb]:duration-150',
                      '[&::-webkit-slider-thumb]:hover:scale-110 [&::-webkit-slider-thumb]:hover:border-accent'
                    )}
                  />
                  <div className="flex justify-between text-xs text-text-muted">
                    <span>My Goals</span>
                    <span>Full-Body Form</span>
                  </div>
                  <p className="text-xs text-text-muted">
                    How much your specific annotation goals matter (left) vs. overall
                    body shape similarity to the template (right).
                  </p>
                  {/* D-A (b) — quiet, one line, subdued. Not a banner: the
                      finalize report carries the full three-sentence warning. */}
                  {noWeightForCheckpoints && (
                    <p
                      data-testid="checkpoints-carry-no-weight"
                      className="text-xs text-warning/80"
                    >
                      {CHECKPOINTS_CARRY_NO_WEIGHT_LINE}
                    </p>
                  )}
                </div>
              )}

              {/* Economy of Motion slider */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label
                    className="text-sm font-medium text-text-secondary"
                    title="How much effort efficiency matters — penalizes unnecessary movement or wasted motion"
                  >
                    Economy of Motion
                  </label>
                  <span className="text-sm font-semibold text-text-primary">
                    {eomPct}%
                  </span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={0.3}
                  step={0.01}
                  value={eomWeight}
                  onChange={handleEomSlider}
                  aria-label="Economy of motion weight — how much efficient movement matters"
                  className="w-full accent-accent"
                />
                <div className="flex justify-between text-xs text-text-muted">
                  <span>Disabled</span>
                  <span>30%</span>
                </div>
                <p className="text-xs text-text-muted">
                  How much effort efficiency matters. Higher values penalize athletes
                  who have unnecessary movement or move differently than the reference.
                  0% disables Economy of Motion scoring.
                </p>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ScoringEmphasisPanel;
